import { Typography } from '@mui/material';
import React, { FC, useMemo } from 'react';
import {
  formatCrypto,
  formatFiat,
  formatCurrency,
  DEFAULT_FORMAT_CURRENCY_PRECISION,
} from 'shared/utils';
import { MutedText } from '../MutedText';
import { useStyles } from './FormatCurrency.styles';
import classNames from 'classnames';
import { FormatCurrencyType } from './format-currency-type.enum';
import { DefaultText } from '../DefaultText';
import { SystemCurrency } from '@okletsplay/core';

export interface FormatCurrencyProps {
  amount: number;
  type: FormatCurrencyType;
  currency: SystemCurrency | 'USD';
  showZeroLabel?: boolean;
  zeroLabel?: string;
  showSign?: ConstrainBoolean;
  doColorSign?: boolean;
  inParentheses?: boolean;
  noColor?: boolean;
  variant?: 'base' | 'title';
  maxPrecision?: number;
}

export const FormatCurrency: FC<FormatCurrencyProps> = ({
  showZeroLabel = false,
  zeroLabel = 'Free',
  showSign = true,
  doColorSign = false,
  inParentheses = false,
  amount,
  type,
  currency,
  variant = 'base',
  noColor = false,
  maxPrecision = DEFAULT_FORMAT_CURRENCY_PRECISION,
}) => {
  const { classes } = useStyles();

  const amountText = useMemo(() => {
    const amountAbs = Math.abs(amount);
    switch (type) {
      case FormatCurrencyType.Fiat:
        return formatFiat(amountAbs);
      case FormatCurrencyType.FiatCode:
        return formatFiat(amountAbs, 'code');
      case FormatCurrencyType.Crypto:
        return formatCrypto(amountAbs, currency, maxPrecision);
      default:
        return formatCurrency(amountAbs);
    }
  }, [amount, currency, type, maxPrecision]);

  if (showZeroLabel && amount === 0) {
    return <>{zeroLabel}</>;
  }

  if (variant === 'title') {
    return (
      <>
        {amountText}
        <Typography component="span" variant="subtitle1" className={classes.titleVariant}>
          {currency}
        </Typography>
      </>
    );
  }

  const RootComponent = [FormatCurrencyType.Fiat, FormatCurrencyType.FiatCode].includes(type)
    ? MutedText
    : DefaultText;

  return (
    <RootComponent
      className={classNames(classes.root, {
        [classes.positiveAmount]: doColorSign && amount > 0,
        [classes.negativeAmount]: doColorSign && amount < 0,
        [classes.noColor]: noColor,
      })}
    >
      {inParentheses && <MutedText>{'('}</MutedText>}
      {showSign && amount < 0 && '-'}

      {amountText}

      {inParentheses && <MutedText>{')'}</MutedText>}
    </RootComponent>
  );
};
